import React, { useEffect, useState } from "react";
import { Wallet, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

export const PayoutsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ trading_account_id: "", amount: "", wallet_address: "", currency: "USDT" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/accounts").then((r) => setAccounts((r.data || []).filter((a) => a.status === "Funded")));
    api.get("/payouts").then((r) => setPayouts(r.data || []));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payouts/request", { ...form, amount: parseFloat(form.amount) });
      toast.success("Payout request submitted");
      setOpen(false);
      setForm({ trading_account_id: "", amount: "", wallet_address: "", currency: "USDT" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payouts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Payouts</h1>
          <p className="text-[#8a8a8a] text-sm mt-1">Request and track your crypto payouts.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={accounts.length === 0} className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="payouts-request-button">
              <Plus size={16} className="mr-1.5" /> Request Payout
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]" data-testid="payouts-request-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Request Payout</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Funded Account</Label>
                <Select value={form.trading_account_id} onValueChange={(v) => setForm({ ...form, trading_account_id: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 mt-2 h-11" data-testid="payouts-account-select">
                    <SelectValue placeholder="Select funded account" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id} className="text-[#f5f5f5]">
                        {a.platform} · ${a.account_size.toLocaleString()} ({a.login_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Amount (USD)</Label>
                <Input type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2 h-11" data-testid="payouts-amount-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#ff7a00]/20 mt-2 h-11" data-testid="payouts-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]">
                    {["USDT", "BTC", "ETH"].map((c) => <SelectItem key={c} value={c} className="text-[#f5f5f5]">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Wallet Address</Label>
                <Input required value={form.wallet_address} onChange={(e) => setForm({ ...form, wallet_address: e.target.value })} className="bg-[#050505] border-[#ff7a00]/20 mt-2 h-11 font-mono text-sm" placeholder="0x..." data-testid="payouts-wallet-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="payouts-submit">
                {busy ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {accounts.length === 0 && (
        <div className="glass rounded-xl p-6 text-sm text-[#8a8a8a]">
          You need a funded account to request payouts. Pass an evaluation first.
        </div>
      )}

      <div className="glass rounded-xl overflow-hidden" data-testid="payouts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/15">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Amount</th>
              <th className="text-left p-4">Currency</th>
              <th className="text-left p-4">Wallet</th>
              <th className="text-left p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {payouts.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#8a8a8a]"><Wallet className="mx-auto text-[#ff7a00] mb-3" size={28} />No payouts yet.</td></tr>
            )}
            {payouts.map((p) => (
              <tr key={p.id} className="border-b border-[#ff7a00]/5 hover:bg-white/2" data-testid={`payout-row-${p.id}`}>
                <td className="p-4 text-[#cfcfcf]">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-4 font-display font-bold text-[#ff7a00]">${p.amount.toLocaleString()}</td>
                <td className="p-4 text-[#cfcfcf]">{p.currency}</td>
                <td className="p-4 text-[#8a8a8a] font-mono text-xs truncate max-w-[180px]">{p.wallet_address}</td>
                <td className="p-4">
                  <Badge className={`uppercase text-[10px] tracking-widest ${
                    p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                    p.status === "approved" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
                    p.status === "rejected" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                    "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                  }`}>{p.status}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
